<template>
  <div>
    <success-snackbar
      v-model="snackbar"
      :message="$t('shop.addedToCart')"
    />
    <v-snackbar
      v-model="errorSnackbar"
      color="error"
      :timeout="5000"
    >
      {{ errorMessage }}
      <template #action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="errorSnackbar = false"
        >
          {{ $t('common.close') }}
        </v-btn>
      </template>
    </v-snackbar>
    <ApolloMutation
      :mutation="(gql) => addItemToOrderQuery"
      :variables="{
        itemId: id,
        orderId: orderId,
        locale: countryCode,
      }"
      :update="updateStore"
      @done="onDone"
      @error="onError"
    >
      <template #default="{ mutate, loading }">
        <PrimaryButton
          v-if="buyNow || !inCart || !proceedToCheckout || alwaysShow"
          v-bind="$attrs"
          :class="buttonClasses"
          :disabled="loading || queryLoading"
          :loading="loading || queryLoading"
          elevation="0"
          @click="() => {
            if (!canAdd) {
              changeShowCartDrawer(true);
            }
            else {
              mutate();
            }
          }"
        >
          {{ alwaysShow ? addToCartText : (inCart && canAdd ? ($t('shop.addToCart')+` (${quantity})`) : inCart ? inCartText : addToCartText) }}
          <ekitabu-icon
            v-if="showIcon"
            :class="iconClasses"
          >
            {{ icon }}
          </ekitabu-icon>
        </PrimaryButton>
      </template>
    </ApolloMutation>
  </div>
</template>

<script>
import i18n from '@/i18n';
import OrderQueries from "@/graphql/OrderQueries";
import SuccessSnackbar from '@/components/SuccessSnackbar.vue';
import gql from "graphql-tag";
import { FORMATS } from "@/utils/Const";
import PrimaryButton from '@/components/buttons/PrimaryButton.vue';
import { mapActions } from 'vuex';
import { gtagAddToCartEvent } from "@/helpers/gtagHelper";
import { buildBookUrl } from "@/helpers/slugHelper";

export default {
  components: {
    SuccessSnackbar,
    PrimaryButton,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    simplifiedId: {
      type: String,
      required: true,
    },
    queryLoading: {
      type: Boolean,
      required: true,
    },
    inCartText: {
      type: String,
      required: false,
      default: i18n.t("shop.viewCart"),
    },
    addToCartText: {
      type: String,
      required: false,
      default: i18n.t("shop.addToCart"),
    },
    proceedToCheckout: {
      type: Boolean,
      required: false,
      default: false,
    },
    buttonClasses: {
      type: String,
      default: null,
    },
    showCart: {
      type: Boolean,
      default: false,
    },
    iconClasses: {
      type: String,
      default: 'ml-2 has-glow',
    },
    icon: {
      type: String,
      default: '$cart',
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
    alwaysShow: {
      type: Boolean,
      default: false,
    },
    buyNow: {
      type: Boolean,
      default: false,
    },
    book: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      snackbar: false,
      errorSnackbar: false,
      errorMessage: '',
    }
  },
  apollo: {
    order: {
      query: gql`
        query getCartItems($orderId: ID!) {
          order(id: $orderId) {
            id
            orderLines(first: 1000) {
              edges {
                node {
                  id
                  productId
                  format
                  quantity
                }
              }
            }
          }
        }
      `,
      variables() {
        return {
          orderId: this.orderId,
        };
      },
      skip() {
        return this.buyNow || !this.orderId;
      },
      fetchPolicy: 'cache-and-network',
    },
  },
  computed: {
    addItemToOrderQuery() {
      return OrderQueries.addItemToOrder;
    },
    orderId() {
      return this.buyNow ? null : this.$store.state.cart.currentCart;
    },
    inCart() {
      if (this.order != null && this.orderId) {
        return this.order.orderLines.edges.find((edge) => edge.node.productId === this.simplifiedId);
      }

      return null;
    },
    canAdd() {
      if (this.inCart) {
        return this.inCart.node.format === FORMATS.print;
      }
      return true;
    },
    quantity() {
      if (this.inCart) {
        return this.order.orderLines.edges.find((edge) => edge.node.productId === this.simplifiedId).node.quantity;
      }

      return 0;
    },
    countryCode() {
      return this.$store.state.locale.countryCode;
    },
  },
  methods: {
    ...mapActions('siteFunctionality', ['changeShowCartDrawer']),
    async updateStore(store, {data: {addItemToOrder: {order}}}) {
      if (this.buyNow) {
        this.$store.commit('setBuyNowBook', {
          id: this.id,
          _id: this.simplifiedId,
          returnPath: this.$route.fullPath,
          bookRoute: buildBookUrl({ ...this.book, id: this.id }),
        });
        this.$store.commit('setBuyNowOrder', order.id);
        return;
      }
      if (order.id !== this.orderId) {
        this.$store.commit("setCurrentCart", order.id);
        this.$store.commit('setCartOwner', this.$store.state.auth.getMe);
      }
      // Write the updated order lines into the getCartItems cache
      // so all AddToCartButton instances reflect the new cart state
      try {
        const cartQuery = gql`
          query getCartItems($orderId: ID!) {
            order(id: $orderId) {
              id
              orderLines(first: 1000) {
                edges {
                  node {
                    id
                    productId
                    format
                    quantity
                  }
                }
              }
            }
          }
        `;
        store.writeQuery({
          query: cartQuery,
          variables: { orderId: order.id },
          data: { order },
        });
      } catch (e) {
        // Cache write can fail if fields are missing; non-critical
      }
    },
    onError(error) {
      this.errorMessage = error?.message || this.$t('errors.general');
      this.errorSnackbar = true;
    },
    onDone() {
      if (this.buyNow) {
        this.$router.push({ name: 'checkout', query: { flow: 'buyNow' } });
        return;
      }
      // Mark cart data as stale so Cart.vue refetches, but keep existing data
      // visible to avoid a flash of empty cart while the network request is in flight
      this.$store.commit('markCartDataStale');
      this.$store.dispatch('fetchCartData', {
        apolloClient: this.$apollo.provider.defaultClient,
        forceNetwork: true,
      }).catch(() => {});
      if (this.book) {
        gtagAddToCartEvent(this.book);
      }
      if (this.proceedToCheckout) {
        this.$router.push({ name: 'checkout' });
      } else {
        if (this.showCart) {
          this.changeShowCartDrawer(true);
        }
        this.snackbar = true;
      }
    },
  },
};
</script>
